import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  PlusCircleOutlined,
  ShopOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  Empty,
  Form,
  Table,
  message,
  Tooltip,
  Popconfirm,
  TreeSelect,
  Select,
  Space,
  DatePicker,
  Typography,
  Badge,
  Card,
} from "antd";
import {
  deletePointPromotion,
  getListPointPromotion,
  statusPointPromotion,
} from "../../../api/pointPromotion/pointPromotion";
import useStoreData from "../../../hooks/useStoreData";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { useAsync } from "../../../lib/useAsync";
import { errorMessage } from "../../../lib/apiError";
import { transformData } from "../../../utils/recursive";
import dayjs from "dayjs";
import ViewPointPromotion from "./ViewPointPromotion";
import ButtonWithPermission from "../../../components/ButtonWithPermission/ButtonWithPermission";
import SwitchWithPermission from "../../../components/ButtonWithPermission/SwitchWithPermission";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ImportExportMenu from "../../../components/cms/ImportExportMenu";
import StatusTag from "../../../components/cms/StatusTag";
import ListToolbar from "../../../components/cms/ListToolbar";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import {
  neutral,
  brand,
  semantic,
  spacing,
  typography,
  radius,
  border,
  shadow,
} from "../../../theme/tokens";

const FILTER_DEFS = [
  { key: "branch", label: "Đơn vị" },
  { key: "type", label: "Phạm vi áp dụng" },
  { key: "status", label: "Trạng thái" },
  { key: "date", label: "Thời gian" },
];

// Cột bật/tắt được — `fixed: true` là cột luôn hiện (tên chương trình + thao tác).
const COLUMN_DEFS = [
  { key: "name", label: "Tên chương trình", fixed: true },
  { key: "service_branch_id", label: "Đơn vị" },
  { key: "type", label: "Gắn sản phẩm" },
  { key: "time_period", label: "Thời gian chương trình" },
  { key: "operation", label: "Thao tác", fixed: true },
];

const DEFAULT_VISIBLE_COLUMNS = ["service_branch_id", "type", "time_period"];

export default function PointPromotionTable() {
  // Phân trang cục bộ — slice `pointPromotion` chỉ màn này đọc nên không cần toàn cục.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();

  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [form] = Form.useForm();
  const [branchIdSearch, setBranchIdSearch] = useState(null);
  const [optionStatus, setOptionStatus] = useState(null);
  const [optionType, setOptionType] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const { Text } = Typography;
  const { RangePicker } = DatePicker;
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  const [isModalOpenGen, setIsModalOpenGen] = useState(false);
  const [recordGenQrcode, setRecordGenQrcode] = useState(null);

  const navigate = useNavigate();


  const dataTree = transformData(allListStore?.data);
  const options = dataTree?.filter((item) => item.parent_id === 0);
  const handleSearch = (value = null) => {
    setBranchIdSearch(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    navigate('/add-point-promotion');
  };


  const query = useAsync(
    (signal) =>
      getListPointPromotion(
        {
          currentPage,
          pageSize,
          mcouponBranchId: branchIdSearch,
          status: optionStatus,
          type: optionType,
          selectedDates,
        },
        signal
      ),
    [currentPage, pageSize, branchIdSearch, optionStatus, optionType, selectedDates]
  );

  const listPointPromotions = query.data?.data || [];
  const isLoadPointPromotion = query.loading;
  const totalRecords = query.data?.total || 0;


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSizeChange = useCallback((current, size) => {
    // Trước đây gọi `setPageSize(size)` mà quên `dispatch` -> chỉ tạo ra một object
    // action rồi vứt đi, nên đổi số dòng/trang không có tác dụng. Nay là setState thật.
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const deleteEventRecord = async (id) => {
    try {
      await deletePointPromotion(id);
      message.success("Xóa chương trình tích điểm thành công!");
      // Nạp lại đúng bộ lọc đang áp dụng (bản cũ gọi lại với tham số trống).
      query.reload();
    } catch (err) {
      message.error(errorMessage(err, "Không thể xóa chương trình tích điểm."));
    }
  };

  const [recordEdit, setRecordEdit] = useState({});

  const handleViewDetail = (record) => {
    setRecordEdit(record);
    setIsModalOpenEdit(true);
  };

  const handleEditRecord = (record) => {
    navigate(`/edit-point-promotion/${record.id}`);
  };

  const handleCancelEdit = useCallback(() => {
    setIsModalOpenEdit(false);
  }, [setIsModalOpenEdit]);

  const handleStatus = (value) => {
    setOptionStatus(value);
    setCurrentPage(1);
  };

  const handleType = (value) => {
    setOptionType(value);
    setCurrentPage(1);
  };

  const handleDateChange = (dates, dateStrings) => {
    setSelectedDates(dateStrings);
    setCurrentPage(1);
  };

  /**
   * `ListToolbar` gọi khi ẩn một ô lọc — phải xoá luôn GIÁ TRỊ của nó, không thì
   * điều kiện lọc vẫn còn hiệu lực mà trên màn không còn ô nào hiển thị.
   */
  const clearFilterValue = (key) => {
    if (key === "branch") setBranchIdSearch(null);
    if (key === "type") setOptionType(null);
    if (key === "status") setOptionStatus(null);
    if (key === "date") setSelectedDates([]);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setBranchIdSearch(null);
    setOptionStatus(null);
    setOptionType(null);
    setSelectedDates([]);
    setCurrentPage(1);
    // Không gọi lại API ở đây: `useAsync` đã theo dõi đúng các state trên nên tự
    // fetch lại. Bản cũ gọi thêm một lần nữa -> hai request cho một thao tác.
  };

  
  const handleCancelGen = useCallback(() => {
    setIsModalOpenGen(false);
  }, [setIsModalOpenGen]);

  const handleRecordGenQrcode = (record) => {
    setRecordGenQrcode(record);
    setIsModalOpenGen(true);
  };

  // Thêm hàm xử lý thay đổi trạng thái
  const handleStatusChange = async (record, checked) => {
    try {
      checked = checked === 1 ? true : false;
      await statusPointPromotion({ id: record.id, data: { is_active: checked } });
      message.success("Cập nhật trạng thái thành công!");
      query.reload();
    } catch (err) {
      message.error(errorMessage(err, "Cập nhật trạng thái thất bại!"));
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "Tên chương trình",
      dataIndex: "name",
      key: "name",
      fixed: 'left',
      width: 200,
      ellipsis: true,
      render: (name, record) => (
        <a
          style={{
            color: brand.accent,
            fontWeight: 500,
            cursor: 'pointer',
            textDecoration: 'none'
          }}
          onClick={() => handleViewDetail(record)}
        >
          {name}
        </a>
      ),
    },
    {
      title: "Đơn vị",
      dataIndex: "service_branch_id",
      key: "service_branch_id",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <Link 
          to={`/store-manager?search=${encodeURIComponent(record.mcoupon_branch?.name || "")}`} 
          style={{ color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <ShopOutlined style={{ color: neutral[400] }} />
          <Text className="hover:text-cyan-600 transition-colors">
            {record.mcoupon_branch?.name || "Không có dữ liệu"}
          </Text>
        </Link>
      ),
    },
    {
      title: "Gắn sản phẩm",
      dataIndex: "type",
      key: "type",
      align: "center",
      width: 150,
      render: (type) => {
        return type === "PRODUCT" ? (
          <StatusTag>Có</StatusTag>
        ) : type === "OTHER" ? (
          <StatusTag>Không</StatusTag>
        ) : (
          ""
        );
      },
    },
    /* Ẩn cột "Chương trình đồng thời" (promotion_type) — tính năng chưa dùng.
    {
      title: "Chương trình đồng thời",
      dataIndex: "promotion_type",
      key: "promotion_type",
      align: "center",
      width: 200,
      render: (promotionType) => {
        return promotionType === "CHOOSABLE" ? (
          <StatusTag>Có</StatusTag>
        ) : promotionType === "DEFAULT" ? (
          <StatusTag>Không</StatusTag>
        ) : (
          ""
        );
      },
    },
    */
    {
      title: "Thời gian chương trình",
      dataIndex: "time_period",
      key: "time_period",
      width: 400,
      render: (_, record) => {
        const currentDate = new Date();
        const startDate = new Date(record.start_date);
        const endDate = new Date(record.end_date);

        // Kiểm tra trạng thái thời gian
        let status, color, badgeStatus;
        if (currentDate < startDate) {
          status = "Sắp diễn ra";
          color = "#2563EB"; // xanh dương — sắp tới
          badgeStatus = "default";
        } else if (currentDate > endDate) {
          status = "Đã hết hạn";
          color = "#94A3B8"; // xám trung tính — đã kết thúc
          badgeStatus = "default";
        } else {
          status = "Đang diễn ra";
          color = "#16A34A"; // xanh lá — đang chạy
          badgeStatus = "processing";
        }

        // Tính số ngày còn lại
        const timeDiff = endDate - currentDate;
        const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

        return (
          <Space direction="vertical" size={2}>
            <Badge
              status={badgeStatus}
              text={<Text style={{ color, fontWeight: 500 }}>{status}</Text>}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {record.start_date} - {record.end_date}
            </Text>
            {daysDiff > 0 && daysDiff <= 7 && (
              <Text style={{ color: semantic.warning, fontSize: 12, fontWeight: 500 }}>
                Còn {daysDiff} ngày hết hạn
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      key: "operation",
      align: "center",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          {/* Công tắc bật/tắt giữ Popconfirm: đây là thao tác đảo lại được ngay,
              không cần hộp thoại chặn cả màn như hành động xóa. */}
          <Popconfirm
            title="Thay đổi trạng thái"
            description={`Bạn có chắc chắn muốn ${
              record.is_active === 1 ? "hủy kích hoạt" : "kích hoạt"
            } chương trình này?`}
            onConfirm={() =>
              handleStatusChange(record, record.is_active === 0 ? 1 : 0)
            }
            okText="Cập nhật"
            cancelText="Hủy"
            icon={<ExclamationCircleOutlined style={{ color: semantic.warning }} />}
          >
            <Tooltip title={record.is_active === 1 ? "Đang kích hoạt" : "Đang tắt"}>
              <SwitchWithPermission
                actions={["point-promotion.updateStatus"]}
                size="small"
                checked={record.is_active === 1}
                noPermissionText={"Không có quyền truy cập"}
              />
            </Tooltip>
          </Popconfirm>

          <RowActions
            onView={() => handleViewDetail(record)}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                permission: ["point-promotion.update"],
                onClick: () => handleEditRecord(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa chương trình",
                danger: true,
                divider: true,
                permission: ["point-promotion.delete"],
                onClick: () =>
                  confirmDelete(
                    record.name,
                    () => deleteEventRecord(record.id),
                    "Hành động này không thể hoàn tác."
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
  ];

  const displayColumns = columns.filter(
    (c) => c.fixed || visibleColumns.includes(c.key)
  );

  // Bộ lọc đang áp dụng — dùng chung cho export để file tải về khớp bảng đang xem.
  const exportParams = {
    ...(branchIdSearch ? { mcoupon_branch_id: branchIdSearch } : {}),
    ...(optionStatus ? { status: optionStatus } : {}),
    ...(optionType ? { type: optionType } : {}),
    ...(selectedDates && selectedDates.length > 0
      ? { start_date: selectedDates[0], end_date: selectedDates[1] }
      : {}),
  };

  return (
    <BrandedPageLayout
      title="Chương trình tích điểm"
      subtitle="Quản lý các chiến dịch tích điểm, thiết lập quy tắc nhận điểm và theo dõi hiệu quả chương trình."
      breadcrumbItems={[{ title: 'Chương trình', path: '/point-promotion' }, { title: 'Tích điểm' }]}
    >
      <ListToolbar
        filterDefs={FILTER_DEFS}
        filters={{
          branch: (
            <TreeSelect
              style={{ width: "100%" }}
              disabled={isLoadStoreAll}
              placeholder="Đơn vị"
              showSearch
              allowClear
              filterTreeNode={(inputValue, treeNode) =>
                treeNode.title.toLowerCase().includes(inputValue.toLowerCase())
              }
              onChange={handleSearch}
              treeLine
              treeData={options}
              value={branchIdSearch}
            />
          ),
          type: (
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Phạm vi áp dụng"
              onChange={handleType}
              value={optionType}
              options={[
                { value: "PRODUCT", label: "Sản phẩm" },
                { value: "OTHER", label: "Khác" },
              ]}
            />
          ),
          status: (
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Trạng thái"
              onChange={handleStatus}
              value={optionStatus}
              options={[
                { value: "1", label: "Kích hoạt" },
                { value: "0", label: "Không kích hoạt" },
              ]}
            />
          ),
          date: (
            <RangePicker
              style={{ width: "100%" }}
              placeholder={["Từ ngày", "Đến ngày"]}
              onChange={handleDateChange}
              value={
                selectedDates.length
                  ? [dayjs(selectedDates[0]), dayjs(selectedDates[1])]
                  : []
              }
            />
          ),
        }}
        onHideFilter={clearFilterValue}
        onClearFilters={clearAllFilters}
        columnDefs={COLUMN_DEFS}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        loading={isLoadPointPromotion}
        onReload={() => query.reload()}
        actions={
          <>
            <ButtonWithPermission
              actions={["point-promotion.store"]}
              children={"Thêm mới"}
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={handleCreate}
              noPermissionText={"Không có quyền"}
            />
            <ImportExportMenu
              exportUrl="/api/v1/serviceconnect/point-promotion/export"
              exportParams={exportParams}
              exportFileName="chuong_trinh_tich_diem.xlsx"
              exportActions={["point-promotion.index"]}
            />
          </>
        }
      />

      <Form form={form} component={false}>
        <CmsTableCard
          // title="Danh sách chương trình tích điểm"
          // count={isLoadPointPromotion ? 0 : totalRecords}
          columns={displayColumns}
          loading={isLoadPointPromotion}
          dataSource={isLoadPointPromotion ? [] : listPointPromotions}
          rowKey="id"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}–${range[1]} của ${total} chương trình`,
            onShowSizeChange: handleSizeChange,
            total: totalRecords,
            current: currentPage,
            pageSize,
            onChange: handlePageChange,
          }}
          locale={{
            emptyText: <Empty description="Chưa có chương trình nào" style={{ paddingTop: 40 }} />,
          }}
          scroll={{ x: 'max-content' }}
          rowClassName={(record, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
          bordered
        />
      </Form>

      {/*
        Render thường trực để Drawer chạy được hoạt ảnh trượt ra khi đóng (bọc
        `isModalOpenEdit &&` là component bị gỡ ngay, drawer biến mất đột ngột).
        `recordEdit` có thể null lúc chưa chọn dòng nào -> phải optional chaining;
        bên trong `ViewPointPromotion` chỉ gọi API khi có `isModalOpen && record?.id`.
      */}
      <ViewPointPromotion
        isModalOpen={isModalOpenEdit}
        handleCancel={handleCancelEdit}
        record={recordEdit?.id ? { id: recordEdit.id } : null}
      />
    </BrandedPageLayout>
  );
}
